<script setup lang="ts">
import {computed, ref} from "vue";
import {CalendarClock, Moon, Plus} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import {Checkbox} from "@/core/components/ui/checkbox";
import {Skeleton} from "@/core/components/ui/skeleton";
import {cacheKey} from "@/lib/cache";
import useSwr from "@/core/composables/swr/useSwr";
import {customerProgramApi, supportProgramApi} from "@/container";
import {SupportProgramIndexResource} from "@/core/http/resources/SupportProgramResource";
import {Program} from "@/program/interfaces/Program";
import {actRef, actsOfProgram, bookersOf, JourneyAct, MAX_ACTS, untypedActsOf} from "@/agenda/lib/healthJourney";

/**
 * The programs the patient is targeted by, and which of them the journey
 * covers.
 *
 * Only support programs with a series of acts are offered: a program nobody
 * configured acts for has nothing to lay out, and a checkbox that produced no
 * appointment would be a dead end. Targeting itself is read as it stands —
 * it is recomputed nightly, and nothing here asks for it to be run again.
 */
const {consumerKey, actCount} = defineProps<{
    consumerKey: string | null;
    /** Acts already asked for, so the cap can be said before it is hit. */
    actCount: number;
}>();

const emits = defineEmits<{
    /** One act added by hand, outside what the patient is targeted by. */
    (e: 'add', act: JourneyAct): void;
}>();

const selected = defineModel<Program[]>('selected', {required: true});

const key = computed(() => consumerKey ? cacheKey(`consumers/${consumerKey}/programs`) : null);

/**
 * The programs of the patient on screen, and of nobody else.
 *
 * swrv keeps the previous key's data when the key changes — it only flips
 * `isValidating` — so without stamping the payload with the key it was fetched
 * for, the programs of the patient just replaced would stay listed, tickable,
 * under the new patient's name, and the journey would be built from a program
 * they are not targeted by. Anything not stamped with the current key reads as
 * « not loaded yet ».
 */
const {data: payload, error} = useSwr<{key: string; programs: Program[]}>(
    () => key.value,
    async () => {
        const requested = key.value;

        if (!consumerKey || !requested) {
            return {key: '', programs: []};
        }

        return {key: requested, programs: await customerProgramApi().index(consumerKey)};
    }
);

const programs = computed<Program[] | undefined>(() =>
    payload.value?.key === key.value ? payload.value.programs : undefined
);

const loading = computed(() => !!consumerKey && programs.value === undefined && !error.value);

/**
 * Support programs the patient is targeted by that hold a series of acts, and
 * whose notification the pharmacist has not set aside — the very filter the
 * patient file applies to the same list.
 */
const bookable = computed<Program[]>(() =>
    (programs.value ?? []).filter((program) =>
        program.programmable_type === 'support'
        && bookersOf(program).length > 0
        && !program.feedbacks?.find((feedback) => feedback.hide_notification)
    )
);

const isSelected = (program: Program) => selected.value.some((entry) => entry.id === program.id);

/** How many acts a program would add, ignoring what the catalogue cannot type. */
const actsOf = (program: Program) => actsOfProgram(program).length;

/**
 * Whether the program has anything to lay out.
 *
 * A program whose acts all lack an appointment type contributes nothing: the
 * planner is asked for a type, and nothing here can invent one. It stays on
 * the list, because the patient is genuinely targeted by it, but it cannot be
 * ticked — a checkbox that adds no appointment is a dead end.
 */
const plannable = (program: Program) => actsOf(program) > 0;

/**
 * Whether ticking this program would take the journey past what one batch may
 * hold. Said on the control rather than at the end, so nobody builds a series
 * only to be refused it.
 */
const wouldOverflow = (program: Program) =>
    !isSelected(program) && actCount + actsOf(program) > MAX_ACTS;

/**
 * The whole catalogue of bookable programs, for the act a pharmacist adds on
 * their own: targeting answers « de quoi ce patient relève », and there are
 * counter reasons it knows nothing about.
 */
const {data: catalogue} = useSwr<SupportProgramIndexResource[]>(
    'support-programs?bookable=true',
    () => supportProgramApi().index({bookable: true})
);

const extraProgramId = ref<number | null>(null);
const extraBookerId = ref<number | null>(null);

const extraProgram = computed(() =>
    (catalogue.value ?? []).find((program) => program.id === extraProgramId.value) ?? null);

/** Acts of the chosen program that the planner can be asked for. */
const extraBookers = computed(() =>
    (extraProgram.value?.bookers ?? []).filter((booker) => booker.appointment_type_id !== null));

const addExtra = () => {
    const program = extraProgram.value;
    const booker = extraBookers.value.find((entry) => entry.id === extraBookerId.value);

    if (!program || !booker || actCount >= MAX_ACTS) {
        return;
    }

    emits('add', {
        clientRef: actRef(program.id, booker.id),
        programId: program.id,
        programName: program.name,
        bookerId: booker.id,
        booker,
        appointmentTypeId: booker.appointment_type_id as number,
        label: booker.appointment_title || booker.title || program.name
    });

    extraBookerId.value = null;
};

const toggle = (program: Program) => {
    if (isSelected(program)) {
        selected.value = selected.value.filter((entry) => entry.id !== program.id);

        return;
    }

    if (!plannable(program) || wouldOverflow(program)) {
        return;
    }

    selected.value = [...selected.value, program];
};
</script>

<template>
  <section class="rounded-lg bg-white p-4 shadow">
    <div class="mb-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
      <h2 class="text-sm font-semibold text-primary">
        Les programmes à couvrir
      </h2>
      <p class="flex items-center gap-1.5 text-xs text-gray-500">
        <Moon class="size-3.5" />
        Ciblage recalculé chaque nuit
      </p>
    </div>

    <p v-if="!consumerKey" class="text-xs text-gray-500">
      Sélectionnez d'abord un patient.
    </p>

    <div v-else-if="loading" class="flex flex-col gap-2">
      <Skeleton v-for="index in 3" :key="index" class="h-12 w-full" />
    </div>

    <p v-else-if="error" class="text-xs text-gray-600">
      Les programmes du patient n'ont pas pu être chargés. Vérifiez votre connexion, puis réessayez.
    </p>

    <p v-else-if="!bookable.length" class="text-xs text-gray-500">
      Ce patient n'est ciblé par aucun programme comportant des rendez-vous à programmer.
    </p>

    <div v-else class="flex flex-col gap-2">
      <label
        v-for="program in bookable"
        :key="program.id"
        class="flex items-start gap-3 rounded-lg border p-3 transition-colors"
        :class="!plannable(program) || wouldOverflow(program)
          ? 'cursor-not-allowed border-slate-200 opacity-60'
          : 'cursor-pointer border-slate-200 hover:border-primary hover:bg-primary-lightest'"
      >
        <Checkbox
          class="mt-0.5"
          :model-value="isSelected(program)"
          :disabled="!plannable(program) || wouldOverflow(program)"
          @update:model-value="toggle(program)"
        />
        <span class="min-w-0 flex-1">
          <span class="block text-sm font-medium">{{ program.name }}</span>
          <span
            v-if="plannable(program)"
            class="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500"
          >
            <span class="flex items-center gap-1">
              <CalendarClock class="size-3.5" />
              {{ actsOf(program) }} rendez-vous
            </span>
            <span v-for="act in actsOfProgram(program)" :key="act.clientRef">
              · {{ act.label }}
              <template v-if="act.booker?.min_days_after_previous">
                (à {{ act.booker.min_days_after_previous }} j)
              </template>
            </span>
          </span>
          <span v-else class="mt-0.5 block text-xs text-gray-600">
            Aucun rendez-vous à planifier : les {{ untypedActsOf(program) }} acte(s) de ce programme
            n'ont pas de type de rendez-vous. Renseignez-le dans l'administration des programmes.
          </span>
          <span
            v-if="plannable(program) && untypedActsOf(program)"
            class="mt-0.5 block text-xs text-gray-500"
          >
            {{ untypedActsOf(program) }} acte(s) de ce programme n'ont pas de type de rendez-vous :
            ils ne peuvent pas être planifiés.
          </span>
          <span v-if="wouldOverflow(program)" class="mt-0.5 block text-xs text-gray-600">
            Ajouter ce programme dépasserait les {{ MAX_ACTS }} rendez-vous d'un même parcours.
          </span>
        </span>
      </label>
    </div>

    <div v-if="consumerKey" class="mt-3 border-t border-slate-100 pt-3">
      <p class="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Ajouter un rendez-vous
      </p>
      <div class="flex flex-wrap items-center gap-2">
        <select
          v-model="extraProgramId"
          class="h-9 min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-2 text-sm"
          aria-label="Programme"
          @change="extraBookerId = null"
        >
          <option :value="null">
            Programme…
          </option>
          <option v-for="program in catalogue ?? []" :key="program.id" :value="program.id">
            {{ program.name }}
          </option>
        </select>
        <select
          v-model="extraBookerId"
          class="h-9 min-w-0 flex-1 rounded-md border border-slate-200 bg-white px-2 text-sm"
          aria-label="Acte"
          :disabled="!extraBookers.length"
        >
          <option :value="null">
            Acte…
          </option>
          <option v-for="booker in extraBookers" :key="booker.id" :value="booker.id">
            {{ booker.appointment_title || booker.title }}
          </option>
        </select>
        <Button
          variant="outline"
          size="icon"
          aria-label="Ajouter ce rendez-vous"
          :disabled="extraBookerId === null || actCount >= MAX_ACTS"
          @click="addExtra"
        >
          <Plus class="size-4" />
        </Button>
      </div>
    </div>
  </section>
</template>
